package br.com.texeratx.boardgametimeapp.ui.utils

/**
 * Converte data da API (ISO yyyy-MM-dd) para UI (dd/MM/yyyy).
 * Retorna "-" se vier nulo/blank/inválido.
 */
fun apiToUiDate(date: String?): String {
    val iso = date?.trim().orEmpty()
    if (iso.isBlank()) return "-"

    val datePart = iso.substringBefore('T') // tolera datetime (yyyy-MM-dd'T'HH:mm)
    val parsed = parseIsoDate(datePart) ?: return "-"
    val dd = parsed.day.toString().padStart(2, '0')
    val mm = parsed.month.toString().padStart(2, '0')
    val yyyy = parsed.year.toString().padStart(4, '0')
    return "$dd/$mm/$yyyy"
}

/**
 * Converte data da UI (dd/MM/yyyy) para API (ISO yyyy-MM-dd).
 * Retorna null se vier nulo/blank/inválido.
 */
fun uiToApiDate(date: String?): String? {
    val raw = date?.trim().orEmpty()
    if (raw.isBlank()) return null

    // state guarda só dígitos: ddMMyyyy
    val normalized = if (raw.length == 8 && raw.all(Char::isDigit)) {
        "${raw.substring(0, 2)}/${raw.substring(2, 4)}/${raw.substring(4, 8)}"
    } else raw

    val parsed = parseUiDate(normalized) ?: return null
    val dd = parsed.day.toString().padStart(2, '0')
    val mm = parsed.month.toString().padStart(2, '0')
    val yyyy = parsed.year.toString().padStart(4, '0')
    return "$yyyy-$mm-$dd"
}

/**
 * Retorna a data de hoje no formato ISO (yyyy-MM-dd).
 * Implementação por plataforma (expect/actual).
 */
expect fun todayApiDate(): String

/**
 * Retorna data+hora atual no formato "yyyy-MM-dd'T'HH:mm".
 * Implementação por plataforma (expect/actual).
 */
expect fun nowApiDateTime(): String

/**
 * Converte epoch millis para "yyyy-MM-dd'T'HH:mm" no fuso local.
 * Implementação por plataforma (expect/actual).
 */
expect fun epochMillisToApiDateTime(epochMillis: Long): String

/**
 * Converte datetime da API (yyyy-MM-dd'T'HH:mm) para UI (dd/MM/yyyy HH:mm).
 * Se não tiver 'T', faz fallback para apiToUiDate.
 */
fun apiToUiDateTime(dateTime: String?): String {
    val raw = dateTime?.trim().orEmpty()
    if (raw.isBlank()) return "-"
    val tIdx = raw.indexOf('T')
    if (tIdx < 0) return apiToUiDate(raw)
    val datePart = apiToUiDate(raw.substring(0, tIdx))
    if (datePart == "-") return "-"
    val timePart = raw.substring(tIdx + 1).take(5)
    return "$datePart $timePart"
}

/** Extrai a parte de hora "HH:mm" de "yyyy-MM-dd'T'HH:mm". Fallback "19:30". */
fun extractTimePart(apiDateTime: String): String {
    val tIdx = apiDateTime.indexOf('T')
    return if (tIdx >= 0) apiDateTime.substring(tIdx + 1).take(5) else "19:30"
}

/** Extrai a parte de data "yyyy-MM-dd" de "yyyy-MM-dd'T'HH:mm". */
fun extractDatePartApi(apiDateTime: String): String {
    val tIdx = apiDateTime.indexOf('T')
    return if (tIdx >= 0) apiDateTime.substring(0, tIdx) else apiDateTime
}

/** Combina data UI (dd/MM/yyyy) + hora (HH:mm) → "yyyy-MM-dd'T'HH:mm". */
fun uiToApiDateTime(uiDate: String?, time: String?): String? {
    val apiDate = uiToApiDate(uiDate) ?: return null
    val t = time?.trim()?.takeIf { it.length == 5 } ?: "19:30"
    return "${apiDate}T${t}"
}

fun currentYearFromTodayApiDate(): Int {
    val date = todayApiDate()

    // caminho rápido (mais comum)
    if (date.length >= 4) {
        val year = date.substring(0, 4).toIntOrNull()
        if (year != null) return year
    }

    // fallback mais robusto (se vier algo diferente tipo "2026/02/01" etc)
    val digits = date.takeWhile { it.isDigit() }
    return digits.toIntOrNull() ?: 1970
}

// -------------------------
// Helpers internos (common)
// -------------------------

private data class SimpleDate(val year: Int, val month: Int, val day: Int)

private fun parseIsoDate(iso: String): SimpleDate? {
    // esperado: yyyy-MM-dd
    if (iso.length != 10) return null
    if (iso[4] != '-' || iso[7] != '-') return null

    val year = iso.substring(0, 4).toIntOrNull() ?: return null
    val month = iso.substring(5, 7).toIntOrNull() ?: return null
    val day = iso.substring(8, 10).toIntOrNull() ?: return null

    return if (isValidDate(year, month, day)) SimpleDate(year, month, day) else null
}

private fun parseUiDate(ui: String): SimpleDate? {
    // esperado: dd/MM/yyyy
    if (ui.length != 10) return null
    if (ui[2] != '/' || ui[5] != '/') return null

    val day = ui.substring(0, 2).toIntOrNull() ?: return null
    val month = ui.substring(3, 5).toIntOrNull() ?: return null
    val year = ui.substring(6, 10).toIntOrNull() ?: return null

    return if (isValidDate(year, month, day)) SimpleDate(year, month, day) else null
}

private fun isValidDate(year: Int, month: Int, day: Int): Boolean {
    if (year !in 1..9999) return false
    if (month !in 1..12) return false
    if (day !in 1..31) return false

    val maxDay = when (month) {
        1, 3, 5, 7, 8, 10, 12 -> 31
        4, 6, 9, 11 -> 30
        2 -> if (isLeapYear(year)) 29 else 28
        else -> return false
    }

    return day <= maxDay
}

private fun isLeapYear(year: Int): Boolean {
    // regra gregoriana
    return (year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)
}

fun minutesToHourMin(totalMinutes: Int): String {
    if (totalMinutes <= 0) return "0min"

    val hours = totalMinutes / 60
    val minutes = totalMinutes % 60

    return when {
        hours == 0 -> "${minutes}min"
        minutes == 0 -> "${hours}h"
        else -> "${hours}h ${minutes}min"
    }
}

fun minutesToHourMinCompact(totalMinutes: Int): String {
    if (totalMinutes <= 0) return "0m"

    val hours = totalMinutes / 60
    val minutes = totalMinutes % 60

    return when {
        hours == 0 -> "${minutes}m"
        minutes == 0 -> "${hours}h"
        else -> "${hours}h${minutes}m"
    }
}
